import React, { useContext, useEffect, useState } from "react";
import {
  DISTRIBUTION_DESCRIPTION_ID,
  DISTRIBUTION_DESCRIPTION_ID_FRENCH,
  DISTRIBUTION_ID,
  DISTRIBUTION_ID_FRENCH,
} from "../../constants/Constants";
import { DistributionSetupFormType } from "../../constants/type";
import { useController, useForm } from "react-hook-form";
import ControlInputBox from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import ControlTextAreaWithCharLimit from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import Button from "@/components/Form/Button/Button";
import { useTranslation } from "react-i18next";
import {
  MotifErrorMessage,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifToast,
} from "@ey-xd/motif-react";
import { useParams } from "react-router-dom";
import ControlSelect from "@/components/Form/SelectBox/ControlSelect";
import { useUpdateDistributionMutation } from "@/services/distribution/distributionReducers";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import FormattedDatePicker from "@/components/FormattedDatePicker/FormattedDatePicker";
import "./DistributionEditForm.scss";
import { useDispatch } from "react-redux";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import DistributionContext from "../../summery/DistributionContext";
import {
  CANCEL_BUTTON_VARIANT,
  CONFIRM_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { getOnlyDate } from "@/utility/common";
import CopyInputBox from "@/components/Form/InputBox/CopyInputBox";

//@ts-ignore
const DistributionEditForm = ({
  distributionData,
  setNotification,
  notification,
  refetch,
  setShouldFetchCasePermissions
}: any) => {
  const [isVisible, setIsVisible] = useState(false);
  const cancelBtnApproveHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setIsVisible(false);
    setShowMessage({ ...showMessage, show: false, type: "error" });
    distributionContextData.setEditDistribution(false);
    setNotification({
      ...notification,
      ...notification,
      show: false,
      message: "",
      supportingMessage: "",
      type: "success",
    });
  };
  const distributionContextData: any = useContext<any>(DistributionContext);

  const cancelBtnRejectHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setIsVisible(false);
    setShowMessage({ ...showMessage, show: false, type: "error" });
    dispatch(setDiscardEditMode({ editMode: true }));
  };
  const cancelConfirmRejectHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setShowConfirmMessage({ show: false, message: "" });
  };

  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isDirty },
  } = useForm<DistributionSetupFormType, any>({
    defaultValues: {
      name: distributionData.name || "",
      nameFrench: distributionData.nameFrench || "",
      description: distributionData.description || "",
      descriptionFrench: distributionData.descriptionFrench || "",
      distributionStatus: distributionData?.distributionStatus.toString(),
      distributionDate: `${distributionData?.distributionDate} `, // not a typo, needs a space to force Motif to use local time
    },
  });
  const { field: distributionDateField } = useController({
    name: "distributionDate",
    control,
    rules: {
      required: {
        value: true,
        message: t(
          "pages.distributions.distribution.validationMessages.distributionDateReq"
        ),
      },
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showMessage, setShowMessage] = useState({
    show: false,
    type: "error",
    message: "",
  });
  const { caseId, distributionId } = useParams<{
    caseId: string;
    distributionId: string;
  }>();
  const payableOptions = [
    {
      value: "1",
      label: t("pages.distributions.distribution.statusOptions.unlocked"),
    },
    {
      value: "2",
      label: t("pages.distributions.distribution.statusOptions.locked"),
    },
    {
      value: "3",
      label: t("pages.distributions.distribution.statusOptions.committed"),
    },
  ];
  const closeMessageHandler = () => {
    setShowMessage({ ...showMessage, show: false, type: "error" });
  };
  const renderMessage = () => {
    if (showMessage.show) {
      window.scrollTo(0, 0);
      return (
        <MotifToast
          onClose={closeMessageHandler}
          //@ts-ignore
          variant={showMessage.type}
        >
          {" "}
          {showMessage.type === "error"
            ? t(showMessage.message)
            : t("global.globalNotifications.updatedSuccessfully")}
        </MotifToast>
      );
    }
  };
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: isDirty }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, isDirty]);
  const [showConfirmMessage, setShowConfirmMessage] = useState({
    show: false,
    message: "",
  });
  const [updateDistribution] = useUpdateDistributionMutation();
  const onSubmit = async (
    data: DistributionSetupFormType,
    confirmDelete: boolean
  ) => {
    setIsLoading(true);
    const requestBody = {
      caseId,
      id: distributionId,
      name: data.name,
      description: data.description,
      nameFrench: data.nameFrench,
      descriptionFrench: data.descriptionFrench,
      distributionStatus: Number(data.distributionStatus),
      distributionDate: data?.distributionDate
        ? getOnlyDate(data.distributionDate)
        : null,
      confirmDeleteDraftPayment: confirmDelete,
    };
    dispatch(setDiscardEditMode({ editMode: false }));

    await updateDistribution(requestBody)
      .then((response: any) => {
        if (response.data) {
          if (response?.data?.isUpdated) {
            setNotification({
              ...notification,
              show: true,
              message: response?.data?.message,
              supportingMessage: "",
              type: "success",
            });
            setShouldFetchCasePermissions(true);
            distributionContextData.setEditDistribution(false);
            refetch();
          } else {
            setShowConfirmMessage({
              show: true,
              message: response?.data?.message,
            });
          }
        } else if (response.error) {
          setShowMessage({
            ...showMessage,
            show: true,
            type: "error",
            message:
              response.error?.data?.message ||
              "global.globalNotifications.unExpectedError",
          });
          setIsLoading(false);
          dispatch(setDiscardEditMode({ editMode: true }));
        }
      })
      .catch((error) => {
        setShowMessage({
          ...showMessage,
          show: true,
          type: "error",
          message:
            error.data?.message || "global.globalNotifications.unExpectedError",
        });
        setIsLoading(false);
        dispatch(setDiscardEditMode({ editMode: true }));
      });
    setIsLoading(false);
  };
  return (
    <>
      {renderMessage()}
      <form onSubmit={handleSubmit((data) => onSubmit(data, false))}>
        <div className="motif-container case-summary-form pad-v-20 mar-t-20 mar-b-80">
          <div className="motif-row justify-content-between align-items-center">
            <div className="motif-col-4">
              <div className="motif-body1-default-regular page-title">
                {t(
                  "pages.distributions.distribution.distributionEditPage.title"
                )}{" "}
              </div>
              <div className="motif-body2-default-regular page-description">
                {t(
                  "pages.distributions.distribution.distributionEditPage.description"
                )}
              </div>
            </div>
          </div>
          <div className="motif-row pad-v-20">
            <div className="motif-col-lg-5 ">
              <div className="form-field ">
                <ControlInputBox
                  name={DISTRIBUTION_ID}
                  control={control}
                  required={true}
                  isTextArea={false}
                  label={
                    "pages.distributions.distribution.distributionFormLabels.distributionName"
                  }
                  showLabel={true}
                />
                {errors[DISTRIBUTION_ID] &&
                  errors[DISTRIBUTION_ID].type === "required" && (
                    <MotifErrorMessage>
                      {t(
                        "pages.distributions.distribution.validationMessages.distributionNameReq"
                      )}
                    </MotifErrorMessage>
                  )}
              </div>
            </div>
            <div className="motif-col-lg-5">
              <div className="form-field">
                <ControlInputBox
                  name={DISTRIBUTION_ID_FRENCH}
                  control={control}
                  isTextArea={false}
                  label={
                    "pages.distributions.distribution.distributionFormLabels.distributionNameFrench"
                  }
                  showLabel={true}
                />
              </div>
            </div>
          </div>

          <div className="motif-row next-row">
            <div className="motif-col-lg-5">
              <div className="form-field">
                <ControlTextAreaWithCharLimit
                  name={DISTRIBUTION_DESCRIPTION_ID}
                  control={control}
                  isTextArea={true}
                  label={
                    "pages.distributions.distribution.distributionFormLabels.description"
                  }
                  showLabel={true}
                />
              </div>
            </div>
            <div className="motif-col-lg-5">
              <div className="form-field ">
                <ControlTextAreaWithCharLimit
                  name={DISTRIBUTION_DESCRIPTION_ID_FRENCH}
                  control={control}
                  isTextArea={true}
                  label={
                    "pages.distributions.distribution.distributionFormLabels.descriptionFrench"
                  }
                  showLabel={true}
                />
              </div>
            </div>

            <div className="motif-col-lg-5">
              <div style={{ minWidth: "200px" }} className="form-field">
                <ControlSelect
                  name={"distributionStatus"}
                  //@ts-ignore
                  control={control}
                  showLabel={true}
                  label={t(
                    "pages.distributions.distribution.distributionFormLabels.status"
                  )}
                  options={payableOptions}
                  required={true}
                  //@ts-ignore
                  setValue={() => {}}
                  //@ts-ignore
                  getValues={getValues}
                  value={getValues("distributionStatus")}
                />
              </div>
            </div>
            <div className="motif-col-lg-5 ">
              <div className="form-field">
                <div className="formFieldLabel">
                  <label htmlFor="input" aria-label={"Distribution Date"}>
                    {t(
                      "pages.distributions.distribution.distributionFormLabels.distributionDate"
                    )}
                    <span className="requiredSymbol">*</span>
                  </label>
                </div>
                <FormattedDatePicker
                  onChange={(value) => {
                    distributionDateField.onChange(value);
                  }}
                  required={true}
                  value={distributionDateField.value}
                  minDate={
                    distributionData?.createdOn
                      ? new Date(getOnlyDate(distributionData?.createdOn))
                      : new Date()
                  }
                  className="distributionFormatedDatePicker"
                />
                {errors.distributionDate &&
                  errors.distributionDate.type === "required" && (
                    <MotifErrorMessage>
                      {t(errors.distributionDate.message || "")}
                    </MotifErrorMessage>
                  )}
              </div>
            </div>
            <div className="motif-col-lg-10 pad-v-20">
              <CopyInputBox
                value={distributionData?.executionLog}
                isReadonly={true}
                label={t(
                  "pages.distributions.distribution.distributionFormLabels.executionLog"
                )}
              />
            </div>
          </div>
        </div>
        <div className="button-container sticky-footer">
          <Button
            label={t("global.globalActions.save")}
            type={isLoading ? "loader" : "submit"}
            variant={SAVE_BUTTON_VARIANT}
            // disabled={isLoading}
          />
          <Button
            type="button"
            label={t("global.globalActions.cancel")}
            variant={CANCEL_BUTTON_VARIANT}
            className="cancelBtn"
            onClickHandler={(e) => {
              if (isDirty) {
                setIsVisible(true);
                setShowMessage({ ...showMessage, show: false, type: "error" });
                dispatch(setDiscardEditMode({ editMode: false }));
              } else {
                cancelBtnApproveHandler(e);
              }
            }}
          />
        </div>
      </form>

      <ConfirmationModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        cancelBtnApproveHandler={cancelBtnApproveHandler}
        cancelBtnRejectHandler={cancelBtnRejectHandler}
      />
      <>
        <MotifModal
          onClose={() => setShowConfirmMessage({ show: false, message: "" })}
          show={showConfirmMessage.show}
          focusTrapOptions={{
            tabbableOptions: {
              displayCheck: "none",
            },
          }}
        >
          <div className="confirmationModalWrapper">
            <MotifModalBody>
              <span className="discard_heading">
                {showConfirmMessage.message}
              </span>
            </MotifModalBody>
            <MotifModalFooter>
              <Button
                label={t("global.globalActions.confirm")}
                variant={CONFIRM_BUTTON_VARIANT}
                className="delBtn"
                type="button"
                onClickHandler={() => {
                  handleSubmit((data) => onSubmit(data, true))();
                  setShowConfirmMessage({ show: false, message: "" });
                }}
              />
              <Button
                label={t("global.globalActions.cancel")}
                variant={CANCEL_BUTTON_VARIANT}
                className="cancelBtnModal"
                type="button"
                onClickHandler={cancelConfirmRejectHandler}
              />
            </MotifModalFooter>
          </div>
        </MotifModal>
      </>
    </>
  );
};

export default DistributionEditForm;
